import React from 'react';
import { Image as ImageIcon, Type, Users, Trash2, Loader2, Save } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Settings } from 'lucide-react';

const FONTS = [
  { name: 'Lora (Serif)', value: 'Lora' },
  { name: 'Merriweather', value: 'Merriweather' },
  { name: 'Crimson Text', value: 'Crimson Text' },
  { name: 'EB Garamond', value: 'EB Garamond' },
  { name: 'Cormorant Garamond', value: 'Cormorant Garamond' },
  { name: 'Libre Baskerville', value: 'Libre Baskerville' },
  { name: 'Playfair Display', value: 'Playfair Display' },
  { name: 'PT Serif', value: 'PT Serif' },
  { name: 'Noto Serif', value: 'Noto Serif' },
  { name: 'Spectral', value: 'Spectral' },
  { name: 'Alegreya', value: 'Alegreya' },
  { name: 'Cardo', value: 'Cardo' },
  { name: 'Old Standard TT', value: 'Old Standard TT' },
  { name: 'Vollkorn', value: 'Vollkorn' },
  { name: 'Literata', value: 'Literata' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro' },
  { name: 'Open Sans', value: 'Open Sans' },
  { name: 'Nunito', value: 'Nunito' },
  { name: 'Raleway', value: 'Raleway' },
  { name: 'Work Sans', value: 'Work Sans' },
  { name: 'Courier Prime (Typewriter)', value: 'Courier Prime' },
  { name: 'Special Elite (Old Typewriter)', value: 'Special Elite' },
  { name: 'Caveat (Handwritten)', value: 'Caveat' },
  { name: 'Dancing Script (Cursive)', value: 'Dancing Script' },
  { name: 'Georgia', value: 'Georgia' },
];

const FONT_SIZES = ['14px', '16px', '18px', '20px', '22px'];

export const EditorSettingsSheet = ({
  story, setStory, genres, isEditing, user,
  coverInputRef, uploadingCover,
  tagInput, setTagInput, addTag, removeTag,
  collabSearch, setCollabSearch, collabResults, setCollabResults,
  handleDelete, api, handleSave, saving
}) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="outline" className="gap-2" data-testid="settings-button">
        <Settings className="w-4 h-4" /> <span className="hidden sm:inline">Settings</span>
      </Button>
    </SheetTrigger>
    <SheetContent>
      <SheetHeader>
        <SheetTitle>Story Settings</SheetTitle>
      </SheetHeader>
      <div className="space-y-6 mt-6">
        <div className="space-y-2">
          <Label>Genre</Label>
          <Select value={story.genre} onValueChange={(value) => setStory(prev => ({ ...prev, genre: value }))}>
            <SelectTrigger data-testid="genre-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {genres.map((genre) => <SelectItem key={genre} value={genre}>{genre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Cover Image</Label>
          <Button type="button" variant="outline" className="w-full gap-2" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}>
            {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            {story.cover_image ? 'Change Cover Photo' : 'Upload Cover Photo'}
          </Button>
          {story.cover_image && (
            <div className="relative">
              <img src={story.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${story.cover_image}` : story.cover_image} alt="Cover preview" className="w-full h-32 object-cover rounded-lg mt-2" />
              <button type="button" onClick={() => setStory(prev => ({ ...prev, cover_image: '' }))} className="absolute top-3 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">x</button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Tags (max 5)</Label>
          <div className="flex gap-2">
            <Input placeholder="Add a tag..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} data-testid="tag-input" />
            <Button type="button" variant="outline" onClick={addTag}>Add</Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {story.tags.map((tag) => (
              <span key={tag} className="px-2 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm flex items-center gap-1">
                #{tag}
                <button onClick={() => removeTag(tag)} className="hover:text-red-500">x</button>
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2"><Type className="w-4 h-4" /> Reading Font</Label>
          <Select value={story.font_family} onValueChange={(value) => setStory(prev => ({ ...prev, font_family: value }))}>
            <SelectTrigger data-testid="font-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FONTS.map((font) => <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Font Size</Label>
          <Select value={story.font_size} onValueChange={(value) => setStory(prev => ({ ...prev, font_size: value }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {FONT_SIZES.map((size) => <SelectItem key={size} value={size}>{size}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {isEditing && (
          <div className="pt-4 border-t space-y-2">
            <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Invite Collaborator</Label>
            <div className="flex gap-2">
              <Input placeholder="Search by name..." value={collabSearch}
                onChange={async (e) => {
                  setCollabSearch(e.target.value);
                  if (e.target.value.length > 0) {
                    try {
                      const res = await api.get(`/users/search?q=${encodeURIComponent(e.target.value)}&limit=5`);
                      setCollabResults(res.data.filter(u => u.id !== user.id));
                    } catch (err) { /* err logged */ }
                  } else { setCollabResults([]); }
                }}
                data-testid="collab-search-input"
              />
            </div>
            {collabResults.length > 0 && (
              <div className="border border-[#E5E2DC] rounded-lg overflow-hidden" data-testid="collab-search-results">
                {collabResults.map(u => (
                  <button key={u.id} type="button"
                    onClick={async () => {
                      try {
                        await api.post('/collaborations/invite', { story_id: story.id, invited_user_id: u.id, message: `Come collaborate on "${story.title}"!` });
                        setCollabSearch(''); setCollabResults([]);
                        alert(`Invitation sent to ${u.display_name || u.username}!`);
                      } catch (err) { alert(err.response?.data?.detail || 'Error sending invite'); }
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-alt text-left text-sm">
                    <span className="font-medium">{u.display_name || u.username}</span>
                    <span className="text-text-muted">@{u.username}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isEditing && (
          <div className="pt-4 border-t">
            <Button className="w-full gap-2 btn-accent" onClick={() => handleSave(false)} disabled={saving} data-testid="settings-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </div>
        )}

        {isEditing && (
          <div className="pt-6 border-t">
            <Button variant="destructive" className="w-full gap-2" onClick={handleDelete} data-testid="delete-button">
              <Trash2 className="w-4 h-4" /> Delete Work
            </Button>
          </div>
        )}
      </div>
    </SheetContent>
  </Sheet>
);
